import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hand,
  AlertTriangle,
  FileText,
  TrendingUp,
  RefreshCw,
  X,
  Eye,
  EyeOff,
  ShieldCheck,
  Users,
  BadgeIndianRupee,
  Banknote,
  Package,
  Shield,
  Hourglass,
  CalendarDays,
  ArrowUpRight,
  ClipboardList,
  CheckCircle2
} from 'lucide-react';
import Toast from '../components/Toast';
import DashboardMobile from '../components/DashboardMobile';
import {
  getBusinessesByUser,
  getDashboardMetrics,
  getCollateralDashboard,
  getOverdueLoans,
  getExpiringLoans
} from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const pctChange = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0) {
    if (cur === 0) return 0;
    return 100;
  }
  return ((cur - prev) / Math.abs(prev)) * 100;
};

const formatTrend = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const texts = t('dashboard');
  const [isQuickValuationOpen, setIsQuickValuationOpen] = useState(false);
  const [purity, setPurity] = useState('24k Gold');
  const [netWeight, setNetWeight] = useState('');
  const [lendingRate, setLendingRate] = useState('6500');
  const [maxLoanValue, setMaxLoanValue] = useState(0);
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);

  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [collateralData, setCollateralData] = useState(null);
  const [expiringLoansData, setExpiringLoansData] = useState([]);
  const [overduePaymentsData, setOverduePaymentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showClosedLoansValue, setShowClosedLoansValue] = useState(false);
  const [showCreatedLoansValue, setShowCreatedLoansValue] = useState(false);
  const [showOverdueValue, setShowOverdueValue] = useState(false);
  const [showActiveValue, setShowActiveValue] = useState(false);
  const [showInHandValue, setShowInHandValue] = useState(false);
  const [showPledgedValue, setShowPledgedValue] = useState(false);
  const [showAwaitingValue, setShowAwaitingValue] = useState(false);
  const [showInterestEarnedValue, setShowInterestEarnedValue] = useState(false);
  const [showLateFeesValue, setShowLateFeesValue] = useState(false);
  const [showAverageLoanSizeValue, setShowAverageLoanSizeValue] = useState(false);
  const [showChipInterest, setShowChipInterest] = useState(false);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const dashboardLoadingRef = useRef(false);
  const businessesLoadingRef = useRef(false);

  const userFirstName = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.firstName || user.first_name || 'User';
    } catch (_) {
      return 'User';
    }
  }, []);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return texts.goodMorning || 'Good Morning';
    if (hour < 17) return texts.goodAfternoon || 'Good Afternoon';
    return texts.goodEvening || 'Good Evening';
  }, [texts.goodMorning, texts.goodAfternoon, texts.goodEvening]);

  const loadDashboardData = useCallback(async () => {
    if (dashboardLoadingRef.current) return;
    dashboardLoadingRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setError('No business selected. Please select a business first.');
        setLoading(false);
        dashboardLoadingRef.current = false;
        return;
      }

      const [metricsRes, collateralRes, expiringRes, overdueRes] = await Promise.allSettled([
        getDashboardMetrics(businessId),
        getCollateralDashboard(businessId),
        getExpiringLoans(businessId, 30),
        getOverdueLoans(businessId)
      ]).then((results) =>
        results.map((r) =>
          r.status === 'fulfilled'
            ? r.value
            : { success: false, message: r.reason?.message || 'Request failed' }
        )
      );

      if (metricsRes.success && metricsRes.data) {
        setDashboardMetrics(metricsRes.data);
      } else {
        console.error('Failed to load dashboard metrics:', metricsRes.message);
      }

      if (collateralRes.success && collateralRes.data) {
        setCollateralData(collateralRes.data);
      } else {
        console.error('Failed to load collateral data:', collateralRes.message);
      }

      if (expiringRes.success && expiringRes.data) {
        setExpiringLoansData(Array.isArray(expiringRes.data) ? expiringRes.data : []);
      } else {
        setExpiringLoansData([]);
      }

      if (overdueRes.success && overdueRes.data) {
        setOverduePaymentsData(Array.isArray(overdueRes.data) ? overdueRes.data : []);
      } else {
        setOverduePaymentsData([]);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
      dashboardLoadingRef.current = false;
    }
  }, []);

  const isMetricsLoading = loading && !dashboardMetrics;

  const healthStatus = useMemo(() => {
    const overdue = dashboardMetrics?.overdueAccounts || 0;
    if (overdue === 0) {
      return {
        label: texts.healthGood || 'Good',
        hint: texts.healthGoodHint || "You're performing well",
        tone: 'good'
      };
    }
    if (overdue <= 5) {
      return {
        label: texts.healthFair || 'Fair',
        hint: texts.healthFairHint || 'A few accounts need attention',
        tone: 'fair'
      };
    }
    return {
      label: texts.healthAttention || 'Attention',
      hint: texts.healthAttentionHint || 'Overdue accounts need follow-up',
      tone: 'attention'
    };
  }, [dashboardMetrics, texts]);

  const primaryKpis = [
    {
      key: 'closed',
      label: texts.todayClosedLoans || 'Today Closed Loans',
      count: dashboardMetrics?.todayClosedLoans ?? 0,
      amount: dashboardMetrics?.todayClosedLoansValue,
      showValue: showClosedLoansValue,
      toggle: () => setShowClosedLoansValue((v) => !v),
      tone: 'green',
      icon: CheckCircle2,
      trend: formatTrend(
        pctChange(dashboardMetrics?.todayClosedLoans, dashboardMetrics?.yesterdayClosedLoans)
      ),
      trendLabel: texts.vsYesterday || 'vs yesterday',
      trendPositive: (dashboardMetrics?.todayClosedLoans || 0) >= (dashboardMetrics?.yesterdayClosedLoans || 0)
    },
    {
      key: 'created',
      label: texts.todayCreatedLoans || 'Today Created Loans',
      count: dashboardMetrics?.todayCreatedLoans ?? 0,
      amount: dashboardMetrics?.todayCreatedLoansValue,
      showValue: showCreatedLoansValue,
      toggle: () => setShowCreatedLoansValue((v) => !v),
      tone: 'blue',
      icon: FileText,
      trend: formatTrend(
        pctChange(dashboardMetrics?.todayCreatedLoans, dashboardMetrics?.yesterdayCreatedLoans)
      ),
      trendLabel: texts.vsYesterday || 'vs yesterday',
      trendPositive: (dashboardMetrics?.todayCreatedLoans || 0) >= (dashboardMetrics?.yesterdayCreatedLoans || 0)
    },
    {
      key: 'overdue',
      label: texts.overdueAccounts || 'Overdue Accounts',
      count: dashboardMetrics?.overdueAccounts ?? 0,
      amount: dashboardMetrics?.overdueAccountsValue,
      showValue: showOverdueValue,
      toggle: () => setShowOverdueValue((v) => !v),
      tone: 'red',
      icon: AlertTriangle,
      trend: formatTrend(
        pctChange(dashboardMetrics?.overdueAccounts, dashboardMetrics?.yesterdayOverdueAccounts)
      ),
      trendLabel: texts.vsYesterday || 'vs yesterday',
      trendPositive: (dashboardMetrics?.overdueAccounts || 0) <= (dashboardMetrics?.yesterdayOverdueAccounts || 0)
    },
    {
      key: 'active',
      label: texts.activeLoans || 'Active Loans',
      count: dashboardMetrics?.activeLoans ?? 0,
      amount: dashboardMetrics?.activeLoansValue,
      showValue: showActiveValue,
      toggle: () => setShowActiveValue((v) => !v),
      tone: 'purple',
      icon: Users,
      trend: formatTrend(
        pctChange(dashboardMetrics?.activeLoans, dashboardMetrics?.lastMonthActiveLoans)
      ),
      trendLabel: texts.vsLastMonth || 'vs last month',
      trendPositive: (dashboardMetrics?.activeLoans || 0) >= (dashboardMetrics?.lastMonthActiveLoans || 0)
    }
  ];

  const financialCards = [
    {
      key: 'interest',
      label: texts.totalInterestEarned || 'Total Interest Earned',
      value: dashboardMetrics?.totalInterestEarnedThisMonth || 0,
      showValue: showInterestEarnedValue,
      toggle: () => setShowInterestEarnedValue((v) => !v),
      tone: 'green',
      icon: BadgeIndianRupee,
      footer: texts.thisMonth || 'This Month',
      trend: formatTrend(
        pctChange(
          dashboardMetrics?.totalInterestEarnedThisMonth,
          dashboardMetrics?.lastMonthInterestEarned
        )
      ),
      trendPositive:
        Number(dashboardMetrics?.totalInterestEarnedThisMonth || 0) >=
        Number(dashboardMetrics?.lastMonthInterestEarned || 0)
    },
    {
      key: 'lateFees',
      label: texts.lateFeesCollected || 'Late Fees Collected',
      value: dashboardMetrics?.lateFeesCollectedThisMonth || 0,
      showValue: showLateFeesValue,
      toggle: () => setShowLateFeesValue((v) => !v),
      tone: 'red',
      icon: ClipboardList,
      footer: texts.thisMonth || 'This Month',
      trend: formatTrend(
        pctChange(dashboardMetrics?.lateFeesCollectedThisMonth, dashboardMetrics?.lastMonthLateFees)
      ),
      trendPositive: false
    },
    {
      key: 'avg',
      label: texts.averageLoanSize || 'Average Loan Size',
      value: dashboardMetrics?.averageLoanSize || 0,
      showValue: showAverageLoanSizeValue,
      toggle: () => setShowAverageLoanSizeValue((v) => !v),
      tone: 'blue',
      icon: TrendingUp,
      footer: texts.activeLoansSubtitle || 'Active Loans',
      trend: formatTrend(
        pctChange(dashboardMetrics?.averageLoanSize, dashboardMetrics?.lastMonthAverageLoanSize)
      ),
      trendPositive:
        Number(dashboardMetrics?.averageLoanSize || 0) >=
        Number(dashboardMetrics?.lastMonthAverageLoanSize || 0)
    }
  ];

  const collateralDisplayData = [
    {
      label: texts.inHandOffice || 'In-Hand (Office)',
      packets: collateralData?.inHand?.packetCount || 0,
      loanValue: collateralData?.inHand?.totalLoanValue || 0,
      color: 'blue',
      type: 'IN_HAND',
      icon: Package,
      isLoading: loading && !collateralData,
      showValue: showInHandValue,
      toggleVisibility: () => setShowInHandValue((v) => !v)
    },
    {
      label: texts.pledged || 'Pledged',
      packets: collateralData?.pledged?.packetCount || 0,
      loanValue: collateralData?.pledged?.totalLoanValue || 0,
      color: 'orange',
      type: 'PLEDGED',
      icon: Shield,
      isLoading: loading && !collateralData,
      showValue: showPledgedValue,
      toggleVisibility: () => setShowPledgedValue((v) => !v)
    },
    {
      label: texts.awaitingRelease || 'Awaiting Release',
      packets: collateralData?.awaiting?.packetCount || 0,
      loanValue: collateralData?.awaiting?.totalLoanValue || 0,
      color: 'purple',
      type: 'AWAITING',
      icon: Hourglass,
      isLoading: loading && !collateralData,
      showValue: showAwaitingValue,
      toggleVisibility: () => setShowAwaitingValue((v) => !v)
    }
  ];

  // Keep mobile-compatible shape
  const collateralForMobile = collateralDisplayData.map((item) => ({
    ...item,
    value: `${item.packets} ${texts.packets || 'Packets'}`,
    details: `${texts.loanValue || 'Loan Value'}: ${formatCurrency(item.loanValue)}`
  }));

  const handleQuickValuationClick = () => setIsQuickValuationOpen(true);

  const handleCollateralCardClick = (type) => {
    if (type === 'IN_HAND') {
      navigate('/loans', { state: { collateralStatusFilter: type } });
      return;
    }
    navigate('/collateral', { state: { filterStatus: type } });
  };

  const calculateMaxLoanValue = useCallback(() => {
    const weight = parseFloat(netWeight);
    const rate = parseFloat(lendingRate);
    if (weight && rate) {
      setMaxLoanValue(weight * rate);
    } else {
      setMaxLoanValue(0);
    }
  }, [netWeight, lendingRate]);

  useEffect(() => {
    calculateMaxLoanValue();
  }, [calculateMaxLoanValue]);

  useEffect(() => {
    const hasShownWelcome = sessionStorage.getItem('welcomeShown');
    if (!hasShownWelcome && userFirstName !== 'User') {
      const timer = setTimeout(() => {
        setShowWelcomeToast(true);
        sessionStorage.setItem('welcomeShown', 'true');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [userFirstName]);

  const loadBusinesses = useCallback(async () => {
    if (businessesLoadingRef.current) return;
    businessesLoadingRef.current = true;
    const stored = localStorage.getItem('user');
    if (!stored) {
      businessesLoadingRef.current = false;
      return;
    }
    try {
      const user = JSON.parse(stored);
      const userId = user.userId || user.id;
      if (!userId) {
        businessesLoadingRef.current = false;
        return;
      }
      const result = await getBusinessesByUser(userId);
      if (result && result.success && Array.isArray(result.data)) {
        try {
          localStorage.setItem('businesses', JSON.stringify(result.data));
          if (result.data.length > 0) {
            localStorage.setItem('selectedBusiness', JSON.stringify(result.data[0]));
          }
        } catch (_) {
          /* ignore */
        }
      }
    } catch (_) {
      /* ignore */
    } finally {
      businessesLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const interestTrend = formatTrend(
    pctChange(
      dashboardMetrics?.totalInterestEarnedThisMonth,
      dashboardMetrics?.lastMonthInterestEarned
    )
  );

  return (
    <div className="dashboard-page dash-v2">
      {showWelcomeToast && (
        <Toast
          message={`Welcome back ${userFirstName}!`}
          type="success"
          duration={7000}
          onClose={() => setShowWelcomeToast(false)}
        />
      )}

      {error && (
        <Toast message={error} type="error" duration={5000} onClose={() => setError(null)} />
      )}

      <DashboardMobile
        texts={texts}
        loading={loading}
        dashboardMetrics={dashboardMetrics}
        formatCurrency={formatCurrency}
        greeting={greeting}
        userFirstName={userFirstName}
        healthStatus={healthStatus}
        primaryKpis={primaryKpis}
        financialCards={financialCards}
        showCreatedLoansValue={showCreatedLoansValue}
        toggleCreatedLoansValue={() => setShowCreatedLoansValue((v) => !v)}
        showClosedLoansValue={showClosedLoansValue}
        toggleClosedLoansValue={() => setShowClosedLoansValue((v) => !v)}
        showInterestEarnedValue={showInterestEarnedValue}
        toggleInterestEarnedValue={() => setShowInterestEarnedValue((v) => !v)}
        showLateFeesValue={showLateFeesValue}
        toggleLateFeesValue={() => setShowLateFeesValue((v) => !v)}
        showChipInterest={showChipInterest}
        toggleChipInterest={() => setShowChipInterest((v) => !v)}
        collateralDisplayData={collateralForMobile}
        expiringLoansData={expiringLoansData}
        overduePaymentsData={overduePaymentsData}
        onQuickValuation={handleQuickValuationClick}
        onCollateralClick={handleCollateralCardClick}
        onViewLoans={() => navigate('/loans')}
        onOpenLoan={(loanId, loan) => navigate(`/loans/${loanId}`, { state: { loan } })}
      />

      <div className="dashboard-desktop">
        {/* Header banner: greeting + inline metrics (image 1 style) */}
        <div className="dash-hero-banner">
          <div className="dash-hero-text">
            <h1 className="dash-hero-title">
              {greeting}, {userFirstName}! <span aria-hidden="true">👋</span>
            </h1>
            <p className="dash-hero-sub">
              {texts.heroSubtitle || "Here's what's happening with your business today."}
            </p>
          </div>

          <div className="dash-banner-metrics">
            <div className={`dash-banner-metric tone-${healthStatus.tone}`}>
              <span className="dash-banner-metric-icon">
                <ShieldCheck size={18} />
              </span>
              <span className="dash-banner-metric-body">
                <span className="dash-banner-metric-label">{texts.businessHealth || 'Business Health'}</span>
                <span className="dash-banner-metric-value">{healthStatus.label}</span>
                <span className="dash-banner-metric-hint">{healthStatus.hint}</span>
              </span>
            </div>

            <div className="dash-banner-metric tone-purple">
              <span className="dash-banner-metric-icon">
                <Users size={18} />
              </span>
              <span className="dash-banner-metric-body">
                <span className="dash-banner-metric-label">{texts.customers || 'Customers'}</span>
                <span className="dash-banner-metric-value">
                  {isMetricsLoading ? '…' : (dashboardMetrics?.totalCustomers ?? 0)}
                </span>
                <span className="dash-banner-metric-hint">
                  {isMetricsLoading
                    ? '…'
                    : `${dashboardMetrics?.activeCustomers ?? 0} ${texts.activeLabel || 'Active'}`}
                </span>
              </span>
            </div>

            <div className="dash-banner-metric tone-blue">
              <span className="dash-banner-metric-icon">
                <FileText size={18} />
              </span>
              <span className="dash-banner-metric-body">
                <span className="dash-banner-metric-label">{texts.totalLoans || 'Total Loans'}</span>
                <span className="dash-banner-metric-value">
                  {isMetricsLoading ? '…' : (dashboardMetrics?.totalLoans ?? 0)}
                </span>
                <span className="dash-banner-metric-hint">
                  {isMetricsLoading
                    ? '…'
                    : `${dashboardMetrics?.activeLoans ?? 0} ${texts.activeLabel || 'Active'}`}
                </span>
              </span>
            </div>

            <div className="dash-banner-metric tone-green">
              <span className="dash-banner-metric-icon">
                <Banknote size={18} />
              </span>
              <span className="dash-banner-metric-body">
                <span className="dash-banner-metric-label">{texts.interestEarnedChip || 'Interest Earned (This Month)'}</span>
                <span className="dash-banner-metric-value-row">
                  <span className="dash-banner-metric-value">
                    {isMetricsLoading
                      ? '…'
                      : showChipInterest
                        ? formatCurrency(dashboardMetrics?.totalInterestEarnedThisMonth)
                        : '₹ ****'}
                  </span>
                  <button
                    type="button"
                    className="dash-eye-btn"
                    onClick={() => setShowChipInterest((v) => !v)}
                    aria-label="Toggle interest"
                  >
                    {showChipInterest ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </span>
                {interestTrend && (
                  <span className={`dash-banner-metric-hint trend ${Number(dashboardMetrics?.totalInterestEarnedThisMonth || 0) >= Number(dashboardMetrics?.lastMonthInterestEarned || 0) ? 'up' : 'down'}`}>
                    ↑ {interestTrend.replace(/^[+-]/, '')} {texts.fromLastMonth || 'from last month'}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Primary row: Quick Valuation (image 3) + 4 KPI cards */}
        <div className="dash-kpi-grid dash-kpi-grid-with-quick">
          <button
            type="button"
            className="dash-quick-card"
            onClick={handleQuickValuationClick}
          >
            <span className="dash-quick-card-icon">
              <Hand size={28} />
            </span>
            <span className="dash-quick-card-label">
              {texts.quickValuation || 'Quick Valuation'}
            </span>
          </button>

          {primaryKpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.key} className={`dash-kpi-card tone-${kpi.tone}`}>
                <div className="dash-kpi-top">
                  <span className="dash-kpi-label">{kpi.label}</span>
                  {kpi.trend && (
                    <span className={`dash-kpi-badge ${kpi.trendPositive ? 'up' : 'down'}`}>
                      {kpi.trend} {kpi.trendLabel}
                    </span>
                  )}
                </div>
                {isMetricsLoading ? (
                  <div className="dash-loading-inline">
                    <RefreshCw size={16} className="spin" />
                    Loading…
                  </div>
                ) : (
                  <>
                    <div className="dash-kpi-mid">
                      <div className="dash-kpi-count">{kpi.count}</div>
                      <span className={`dash-kpi-icon tone-${kpi.tone}`}>
                        <Icon size={18} />
                      </span>
                    </div>
                    <div className="dash-kpi-amount-row">
                      <span className="dash-kpi-amount">
                        {kpi.showValue ? formatCurrency(kpi.amount) : '₹ ********'}
                      </span>
                      <button type="button" className="dash-eye-btn" onClick={kpi.toggle}>
                        {kpi.showValue ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Financial cards */}
        <div className="dash-finance-grid">
          {financialCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.key} className={`dash-finance-card tone-${card.tone}`}>
                <div className="dash-finance-top">
                  <span className={`dash-finance-icon tone-${card.tone}`}>
                    <Icon size={20} />
                  </span>
                  <span className="dash-finance-label">{card.label}</span>
                </div>
                {isMetricsLoading ? (
                  <div className="dash-loading-inline">
                    <RefreshCw size={16} className="spin" />
                    Loading…
                  </div>
                ) : (
                  <>
                    <div className="dash-finance-value-row">
                      <span className="dash-finance-value">
                        {card.showValue ? formatCurrency(card.value) : '₹ ********'}
                      </span>
                      <button type="button" className="dash-eye-btn" onClick={card.toggle}>
                        {card.showValue ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {card.trend && (
                      <div className={`dash-kpi-trend ${card.trendPositive ? 'up' : 'down'}`}>
                        {card.trend} {texts.vsLastMonth || 'vs last month'}
                      </div>
                    )}
                    <div className="dash-finance-footer">{card.footer}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Collateral */}
        <section className="dash-section-block">
          <h2 className="dash-section-heading">{texts.collateralDashboard || 'Collateral Dashboard'}</h2>
          <div className="dash-collateral-grid">
            {collateralDisplayData.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.type}
                  className={`dash-collateral-card ${item.color}`}
                  onClick={() => !item.isLoading && handleCollateralCardClick(item.type)}
                  disabled={item.isLoading}
                >
                  <Icon className="dash-collateral-watermark" size={88} />
                  {item.isLoading ? (
                    <div className="dash-loading-inline light">
                      <RefreshCw size={18} className="spin" />
                      Loading…
                    </div>
                  ) : (
                    <>
                      <div className="dash-collateral-packets">
                        {item.packets} {texts.packets || 'Packets'}
                      </div>
                      <div className="dash-collateral-label">{item.label}</div>
                      <div className="dash-collateral-footer">
                        <span>
                          {texts.loanValue || 'Loan Value'}:{' '}
                          {item.showValue ? formatCurrency(item.loanValue) : '₹ ********'}
                        </span>
                        <span
                          className="dash-collateral-eye"
                          onClick={(e) => {
                            e.stopPropagation();
                            item.toggleVisibility();
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              item.toggleVisibility();
                            }
                          }}
                        >
                          {item.showValue ? <EyeOff size={16} /> : <Eye size={16} />}
                        </span>
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Lists */}
        <div className="dash-lists-grid">
          <div className="dash-list-panel">
            <div className="dash-list-panel-head">
              <h3>{texts.loansNearingExpiry || 'Loans Nearing Expiry (Next 30 Days)'}</h3>
              <button type="button" className="dash-view-all" onClick={() => navigate('/loans')}>
                {texts.viewAll || 'View All'}
              </button>
            </div>
            {loading && expiringLoansData.length === 0 ? (
              <div className="dash-empty-state">
                <RefreshCw size={22} className="spin" />
                <p>Loading…</p>
              </div>
            ) : expiringLoansData.length > 0 ? (
              <div className="dash-list-items">
                {expiringLoansData.slice(0, 6).map((loan, index) => (
                  <button
                    type="button"
                    key={loan.loanId || index}
                    className="dash-list-row"
                    onClick={() => navigate(`/loans/${loan.loanId}`, { state: { loan } })}
                  >
                    <span className="dash-list-avatar">
                      {(loan.borrowerName || 'U').charAt(0).toUpperCase()}
                    </span>
                    <span className="dash-list-main">
                      <span className="dash-list-id">{loan.loanNumber || 'N/A'}</span>
                      <span className="dash-list-name">{loan.borrowerName || 'Unknown'}</span>
                    </span>
                    <span className="dash-list-meta">
                      <span className={`dash-badge ${loan.daysRemaining <= 7 ? 'danger' : 'warn'}`}>
                        {loan.daysRemaining} days left
                      </span>
                      <span className="dash-list-amount">
                        {formatCurrency(loan.totalDue || loan.principalAmount)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="dash-empty-state success">
                <CalendarDays size={40} />
                <p>
                  {texts.noExpiringLoansCaughtUp ||
                    'Great! No loans expiring in the next 30 days. You’re all caught up.'}
                </p>
              </div>
            )}
          </div>

          <div className="dash-list-panel">
            <div className="dash-list-panel-head">
              <h3>{texts.recentOverduePayments || 'Recent Overdue Payments'}</h3>
              <button type="button" className="dash-view-all" onClick={() => navigate('/loans')}>
                {texts.viewAll || 'View All'}
              </button>
            </div>
            {loading && overduePaymentsData.length === 0 ? (
              <div className="dash-empty-state">
                <RefreshCw size={22} className="spin" />
                <p>Loading…</p>
              </div>
            ) : overduePaymentsData.length > 0 ? (
              <div className="dash-list-items">
                {overduePaymentsData.slice(0, 6).map((payment, index) => (
                  <div key={payment.loanId || index} className="dash-list-row static">
                    <span className="dash-list-avatar danger">
                      {(payment.borrowerName || 'U').charAt(0).toUpperCase()}
                    </span>
                    <span className="dash-list-main">
                      <span className="dash-list-id">{payment.loanNumber || 'N/A'}</span>
                      <span className="dash-list-name">{payment.borrowerName || 'Unknown'}</span>
                      {payment.dueDate && (
                        <span className="dash-list-due">
                          Due: {new Date(payment.dueDate).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </span>
                    <span className="dash-list-meta">
                      <span className="dash-badge danger">
                        Overdue by {payment.overdueBy} days
                      </span>
                      <span className="dash-list-amount">{formatCurrency(payment.dueAmount)}</span>
                      <button
                        type="button"
                        className="dash-outline-btn"
                        onClick={() =>
                          navigate(`/loans/${payment.loanId}`, { state: { loan: payment } })
                        }
                      >
                        {texts.viewLoan || 'View Loan'}
                        <ArrowUpRight size={14} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dash-empty-state">
                <p>{texts.noOverduePayments || 'No overdue payments'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isQuickValuationOpen && (
        <div className="modal-overlay dash-modal-overlay" onClick={() => setIsQuickValuationOpen(false)}>
          <div className="modal-content dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-head">
              <h2>{texts.quickValuationCalculator || 'Quick Valuation Calculator'}</h2>
              <button type="button" className="dash-eye-btn" onClick={() => setIsQuickValuationOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dash-modal-body">
              <label>
                {texts.purity || 'Purity'}
                <select value={purity} onChange={(e) => setPurity(e.target.value)} className="modal-select">
                  <option value="24k Gold">24k Gold</option>
                  <option value="22k Gold">22k Gold</option>
                  <option value="18k Gold">18k Gold</option>
                  <option value="Silver">Silver</option>
                </select>
              </label>
              <label>
                {texts.netWeight || 'Net Weight (in grams)'}
                <input
                  type="number"
                  value={netWeight}
                  onChange={(e) => setNetWeight(e.target.value)}
                  placeholder="e.g., 10.5"
                  className="modal-input"
                />
              </label>
              <label>
                {texts.lendingRate || 'Lending Rate (per gram)'}
                <input
                  type="number"
                  value={lendingRate}
                  onChange={(e) => setLendingRate(e.target.value)}
                  className="modal-input"
                />
              </label>
              <div className="dash-modal-result">
                <span>{texts.maximumLoanValue || 'Maximum Loan Value'}</span>
                <strong>₹{maxLoanValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
              </div>
            </div>
            <div className="dash-modal-foot">
              <button type="button" className="dash-outline-btn" onClick={() => setIsQuickValuationOpen(false)}>
                {texts.close || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
